import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Activity, Server, GitBranch, Clock, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mockChartData = [
  { time: '00:00', requests: 800, responseTime: 120 },
  { time: '04:00', requests: 400, responseTime: 95 },
  { time: '08:00', requests: 1200, responseTime: 140 },
  { time: '12:00', requests: 1800, responseTime: 155 },
  { time: '16:00', requests: 2200, responseTime: 165 },
  { time: '20:00', requests: 1500, responseTime: 135 },
  { time: '24:00', requests: 900, responseTime: 110 },
];

export default function DashboardPage() {
  const { getAuthHeaders } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axios.get(`${API_URL}/metrics/dashboard`, {
          headers: getAuthHeaders()
        });
        setMetrics(response.data);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
        // Set default metrics on error
        setMetrics({
          total_deployments: 0,
          successful_deployments: 0,
          success_rate: 100,
          active_environments: 0,
          avg_deploy_time: 0,
          recent_deployments: [],
          current_metrics: {
            request_rate: 0,
            response_time: 0,
            error_rate: 0,
            cpu_usage: 0,
            memory_usage: 0
          }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [getAuthHeaders]);

  const statCards = metrics ? [
    { title: 'Total Deployments', value: metrics.total_deployments, icon: Rocket, color: 'text-primary', trend: '+12%' },
    { title: 'Success Rate', value: `${metrics.success_rate}%`, icon: CheckCircle, color: 'text-accent', trend: '+2.3%' },
    { title: 'Avg Deploy Time', value: `${metrics.avg_deploy_time}s`, icon: Clock, color: 'text-secondary', trend: '-15s' },
    { title: 'Active Environments', value: metrics.active_environments, icon: Server, color: 'text-primary', trend: 'Stable' },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2" data-testid="dashboard-title">
            Command Center
          </h1>
          <p className="text-foreground/60">
            Monitor your deployment infrastructure in real-time
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                          <p className={`font-heading text-3xl font-bold ${stat.color}`} data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                            {stat.value}
                          </p>
                          <p className="text-xs text-accent mt-1">{stat.trend}</p>
                        </div>
                        <div className={`p-3 rounded-lg bg-primary/10`}>
                          <stat.icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Request Rate Chart */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-primary" />
                      Request Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64" style={{ width: '100%', minHeight: '256px' }}>
                      <ResponsiveContainer width="100%" height={256}>
                        <AreaChart data={mockChartData}>
                          <defs>
                            <linearGradient id="requestGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#00F0FF" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
                          <YAxis stroke="#6B7280" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#151929', 
                              border: '1px solid #1F294F',
                              borderRadius: '8px'
                            }}
                            labelStyle={{ color: '#E0E0E0' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="requests" 
                            stroke="#00F0FF" 
                            fill="url(#requestGradient)" 
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Response Time Chart */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="bg-card/50 border-border">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-secondary" />
                      Response Time (ms)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64" style={{ width: '100%', minHeight: '256px' }}>
                      <ResponsiveContainer width="100%" height={256}>
                        <LineChart data={mockChartData}>
                          <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
                          <YAxis stroke="#6B7280" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#151929', 
                              border: '1px solid #1F294F',
                              borderRadius: '8px'
                            }}
                            labelStyle={{ color: '#E0E0E0' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="responseTime" 
                            stroke="#B24BF3" 
                            strokeWidth={2}
                            dot={{ fill: '#B24BF3', strokeWidth: 0 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Recent Deployments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-card/50 border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <GitBranch className="w-5 h-5 text-accent" />
                    Recent Deployments
                  </CardTitle>
                  <Button variant="outline" size="sm" className="border-border text-foreground/60 hover:text-primary">
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {metrics.recent_deployments.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Rocket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No deployments yet. Create your first deployment!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {metrics.recent_deployments.map((deploy, i) => (
                        <div 
                          key={deploy.id}
                          className="flex items-center justify-between p-4 bg-[#0A0E27] rounded-lg border border-border"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${
                              deploy.status === 'success' ? 'bg-accent animate-pulse-glow' :
                              deploy.status === 'failed' ? 'bg-destructive' :
                              deploy.status === 'deploying' ? 'bg-yellow-500 animate-pulse' :
                              'bg-muted-foreground'
                            }`}></div>
                            <div>
                              <p className="font-medium text-white">{deploy.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {deploy.branch} → {deploy.environment}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-foreground/80 capitalize">{deploy.status}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(deploy.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Live Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary animate-pulse" />
                    Live System Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { label: 'Request Rate', value: `${metrics.current_metrics.request_rate}/min`, color: 'primary' },
                      { label: 'Response Time', value: `${metrics.current_metrics.response_time}ms`, color: 'secondary' },
                      { label: 'Error Rate', value: `${metrics.current_metrics.error_rate}%`, color: 'accent' },
                      { label: 'CPU Usage', value: `${metrics.current_metrics.cpu_usage}%`, color: 'primary' },
                      { label: 'Memory', value: `${metrics.current_metrics.memory_usage}GB`, color: 'secondary' },
                    ].map((metric, i) => (
                      <div key={i} className="text-center p-4 bg-[#0A0E27] rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">{metric.label}</p>
                        <p className={`font-heading text-xl font-bold text-${metric.color}`}>
                          {metric.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
